import React, { useEffect, useRef, useState, useCallback } from "react";
import Webcam from "react-webcam";
import axios from "../lib/axios";
import { getDistance } from "geolib";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { API_BASE_URL } from "../config";
import { useLoader } from "../contexts/LoaderContext";
import { useJourney } from "@/contexts/JourneyContext";
import { Loader2, Camera, MapPin, RefreshCw, CheckCircle2, AlertCircle } from "lucide-react"; // Assuming lucide-react is available, or use standard icons

// ----------------------------------------------------------------------
// TYPES
// ----------------------------------------------------------------------

interface BackendCoordinates {
  latitude: number;
  longitude: number;
}

interface AddressDetails {
  address_line1: string;
  address_line2: string;
  address_line3: string;
  landmark: string;
  pincode: string;
}

interface ValidationThresholds {
  THRESHOLD_METERS: number;
  THRESHOLD_ACCURACY: number;
  THRESHOLD_STALE_DATA_MILLISEC: number;
}

interface GeoPosition {
  lat: number;
  lon: number;
  accuracy: number;
  timestamp: number;
}

interface CaptureData {
  dataUrl: string;
  lat: number;
  lon: number;
  distanceMeters: number;
  timestamp: number;
  accuracy: number;
}

// ----------------------------------------------------------------------
// CONSTANTS
// ----------------------------------------------------------------------

const UPLOAD_TIMEOUT_MS = 60000;

const VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  facingMode: "environment",
  width: { ideal: 1920 },
  height: { ideal: 1080 },
};

// ----------------------------------------------------------------------
// SUB-COMPONENTS
// ----------------------------------------------------------------------

const FullPageLoader = ({ message = "Loading..." }: { message?: string }) => (
  <div className="fixed inset-0 bg-black/90 z-50 flex flex-col items-center justify-center text-white p-4">
    <Loader2 className="w-12 h-12 animate-spin mb-4 text-primary" />
    <p className="text-lg font-medium">{message}</p>
  </div>
);

const InstructionsScreen = ({ address, thresholds, onProceed, isChecking }: { address: AddressDetails, thresholds: ValidationThresholds, onProceed: () => void, isChecking: boolean }) => (
  <div className="min-h-screen bg-black text-white flex flex-col p-6 animate-in fade-in duration-300">
    <div className="flex-1 flex flex-col items-center justify-center text-center max-w-lg mx-auto space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">INSTRUCTIONS</h1>

      <div className="mb-2 rounded-lg bg-gray-100 p-3 text-center text-xs text-gray-600 font-bold w-full">
        By proceeding, you consent to the use of your camera, audio, video, and location for identity and location verification only.
      </div>

      <div className="text-sm">
        This photo must be taken at the following address as provided by you in the application:
      </div>

      <div className="bg-white/10 rounded-xl p-4 w-full border border-white/10 backdrop-blur-sm">
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Registered Address</h3>
        <div className="space-y-1 text-sm font-medium">
          <p>{address.address_line1}</p>
          <p>{address.address_line2}</p>
          {address.address_line3 && <p>{address.address_line3}</p>}
          <p>{address.landmark}</p>
          <p className="text-primary pt-1">{address.pincode}</p>
        </div>
      </div>

      <div className="space-y-4 text-sm text-gray-300">
        <div className="flex items-center gap-3 bg-white/5 p-3 rounded-lg">
          <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
          <span className="text-left">Ensure the shop front and name board are clearly visible.</span>
        </div>
        <div className="flex items-center gap-3 bg-white/5 p-3 rounded-lg">
          <MapPin className="w-5 h-5 text-blue-500 shrink-0" />
          <span className="text-left">You must be within <strong>{thresholds.THRESHOLD_METERS} meters</strong> of the shop.</span>
        </div>
      </div>

    </div>

    <div className="pt-6 w-full max-w-lg mx-auto">
      <Button
        size="lg"
        className="w-full text-lg font-semibold h-12"
        onClick={onProceed}
        disabled={isChecking}
      >
        {isChecking ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Requesting Access...
          </>
        ) : "Start Verification"}
      </Button>
      {isChecking && (
        <p className="text-xs text-gray-400 text-center mt-3">
          This can take up to 2 minutes
        </p>
      )}
    </div>
  </div>
);

const PermissionRequestScreen = ({
  type,
  onGrant,
  onInstruction
}: {
  type: "camera" | "location",
  onGrant: () => void,
  onInstruction: () => void
}) => {
  const isCam = type === "camera";
  const Icon = isCam ? Camera : MapPin;
  const title = isCam ? "Camera Access Required" : "Location Access Required";
  const desc = isCam
    ? "We need camera access to capture the shop photo."
    : "We need your precise location to verify you are at the shop.";

  return (
    <div className="fixed inset-0 bg-black/95 z-40 flex flex-col items-center justify-center p-6 text-center text-white">
      <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mb-6 animate-pulse">
        <Icon className="w-10 h-10 text-white" />
      </div>
      <h2 className="text-2xl font-bold mb-3">{title}</h2>
      <p className="text-gray-400 mb-8 max-w-xs mx-auto text-sm leading-relaxed">{desc}</p>

      <div className="flex flex-col gap-3 w-full max-w-xs">
        <Button size="lg" onClick={onGrant} className="w-full bg-white text-black hover:bg-gray-200">
          Allow Access
        </Button>
        <Button variant="outline" onClick={onInstruction} className="w-full bg-white text-black hover:bg-gray-200">
          Instructions For IOS Devices
        </Button>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------
// MAIN PAGE
// ----------------------------------------------------------------------

export default function ShopOnlyPhotoLocVerification({ isBuilderMode = false }: { isBuilderMode?: boolean }) {
  const navigate = useNavigate();
  const webcamRef = useRef<Webcam | null>(null);

  // -- Global Loader (for submission) --
  const { setLoading: setGlobalLoading } = useLoader();
  const { refresh: refreshJourney } = useJourney();

  // -- State --
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [geoPointMissing, setGeoPointMissing] = useState(false);
  const [backendData, setBackendData] = useState<{
    address: AddressDetails;
    thresholds: ValidationThresholds;
    coord: BackendCoordinates | null;
  } | null>(null);

  const [step, setStep] = useState<"instructions" | "permissions" | "capture" | "preview">("instructions");
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(false);

  // Permissions State
  const [permissionStatus, setPermissionStatus] = useState<{
    camera: "unknown" | "granted" | "denied";
    location: "unknown" | "granted" | "denied";
  }>({ camera: "unknown", location: "unknown" });

  // Capture State
  const [position, setPosition] = useState<GeoPosition | null>(null);
  const [capture, setCapture] = useState<CaptureData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [webcamKey, setWebcamKey] = useState(0); // For forcing webcam forceUpdate

  // -- 1. Load Data on Mount --
  useEffect(() => {
    if (isBuilderMode) return;
    const loadData = async () => {
      try {
        setLoadingInitial(true);
        const res = await axios.post(`${API_BASE_URL}/api/get_address_for_taking_shop_only_photo/`);

        const lat = Number(res.data.latitude);
        const lon = Number(res.data.longitude);
        const missing = res.data.geo_point_missing === true || (lat === 0 && lon === 0);
        setGeoPointMissing(missing);

        setBackendData({
          address: {
            address_line1: res.data.address_line1,
            address_line2: res.data.address_line2,
            address_line3: res.data.address_line3,
            landmark: res.data.landmark,
            pincode: res.data.pincode,
          },
          thresholds: {
            THRESHOLD_METERS: res.data.THRESHOLD_METERS ?? 100,
            THRESHOLD_ACCURACY: res.data.THRESHOLD_ACCURACY ?? 100,
            THRESHOLD_STALE_DATA_MILLISEC: res.data.THRESHOLD_STALE_DATA_MILLISEC ?? 30000,
          },
          coord: missing ? null : { latitude: lat, longitude: lon },
        });
      } catch (e) {
        console.error("Init failed", e);
      } finally {
        setLoadingInitial(false);
      }
    };
    loadData();
  }, []);

  // -- 2. Distance Calculation --
  const getDist = (lat: number, lon: number) => {
    if (!backendData?.coord) return 0; // geo_point_missing: treat as 0m (no distance check)
    return Math.round(getDistance({ latitude: lat, longitude: lon }, backendData.coord));
  };

  // -- 3. Location Watcher --
  const watchIdRef = useRef<number | null>(null);

  const startWatching = useCallback(() => {
    if (!navigator.geolocation || !backendData) return;

    if (watchIdRef.current !== null)
      navigator.geolocation.clearWatch(watchIdRef.current);

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setPermissionStatus(prev => ({ ...prev, location: "granted" }));
        // ... (rest of logic maintained by keeping existing code below if not overwritten)
        // Re-injecting logic here since we are replacing the block
        const { THRESHOLD_STALE_DATA_MILLISEC, THRESHOLD_ACCURACY } = backendData.thresholds;

        if (Date.now() - pos.timestamp > THRESHOLD_STALE_DATA_MILLISEC) {
          setGeoError("GPS Signal is weak.");
          return;
        }
        if (pos.coords.accuracy > THRESHOLD_ACCURACY) {
          setGeoError(`Low Accuracy (${Math.round(pos.coords.accuracy)}m). Needs < ${THRESHOLD_ACCURACY}m`);
          return;
        }

        setGeoError(null);
        setPosition({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          timestamp: pos.timestamp,
        });
      },
      (err) => {
        if (err.code === 1)
          setPermissionStatus(prev => ({ ...prev, location: "denied" }));
        setGeoError("Location access unavailable.");
      },
      { enableHighAccuracy: true, maximumAge: 60000, timeout: 30000 }
    );
  }, [backendData]);

  // -- 4. Permission Handlers --
  const handleStartVerification = async () => {
    setIsCheckingPermissions(true);

    // 1. Check Location
    const locPromise = new Promise<boolean>((resolve) => {
      if (!navigator.geolocation) {
        setPermissionStatus(p => ({ ...p, location: "denied" }));
        resolve(false);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        () => {
          setPermissionStatus(p => ({ ...p, location: "granted" }));
          startWatching();
          resolve(true);
        },
        () => {
          setPermissionStatus(p => ({ ...p, location: "denied" }));
          resolve(false);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });

    // 2. Check Camera
    const camPromise = (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach(t => t.stop());
        setPermissionStatus(p => ({ ...p, camera: "granted" }));
        return true;
      } catch {
        setPermissionStatus(p => ({ ...p, camera: "denied" }));
        return false;
      }
    })();

    // Run parallel
    const [locGranted, camGranted] = await Promise.all([locPromise, camPromise]);
    setIsCheckingPermissions(false);

    if (locGranted && camGranted) {
      setStep("capture");
    } else {
      setStep("permissions");
    }
  };

  // Effect to move to capture if everything granted (fallback for manual grant updates)
  useEffect(() => {
    if (
      step === "permissions" &&
      permissionStatus.camera === "granted" &&
      permissionStatus.location === "granted"
    ) {
      setStep("capture");
    }
  }, [permissionStatus, step]);

  const showIOSHelp = (type: "location" | "camera") => {
    if (type === "location") {
      confirm(
        "To enable location access in iOS:\n\n" +
        "1. Go to Settings -> Privacy & Security -> Location Services\n" +
        "2. Activate Location Services\n" +
        "3. Safari Websites -> While Using the App\n\n" +
        "After doing this you may need to close the tab to reflect changes Press OK and close the tab, or Cancel to reload the page."
      )
    } else {
      alert(
        "To enable camera access on iPhone:\n\n" +
        "1. Go to Settings → Privacy & Security → Camera\n" +
        "2. Enable Camera access for Safari / your browser\n" +
        "3. Reload this page after enabling"
      )
    }
  }

  // -- 5. Capture & Submit --
  const handleCapture = useCallback(() => {
    if (!webcamRef.current || !backendData) return;
    // If geo_point is missing, allow capture without position check
    if (!geoPointMissing && !position) return;
    const screenshot = webcamRef.current.getScreenshot();
    if (!screenshot) return;

    setCapture({
      dataUrl: screenshot,
      lat: position?.lat ?? 0,
      lon: position?.lon ?? 0,
      distanceMeters: position ? getDist(position.lat, position.lon) : 0,
      accuracy: position?.accuracy ?? 0,
      timestamp: Date.now()
    });
    setStep("preview");
  }, [position, backendData, geoPointMissing]);

  const handleSubmit = async () => {
    if (!capture) return;
    setIsSubmitting(true);
    setGlobalLoading(true);

    const form = new FormData();
    // Blob conversion
    const [meta, base64] = capture.dataUrl.split(",");
    const byteStr = atob(base64);
    const ab = new ArrayBuffer(byteStr.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteStr.length; i++) ia[i] = byteStr.charCodeAt(i);
    const blob = new Blob([ab], { type: meta.split(":")[1].split(";")[0] });

    form.append("file", blob, `shop-only-${capture.timestamp}.jpg`);
    form.append("latitude", String(capture.lat));
    form.append("longitude", String(capture.lon));
    form.append("distance_meters", String(capture.distanceMeters));
    form.append("accuracy", String(capture.accuracy));

    try {
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
      const res = await axios.post(`${API_BASE_URL}/api/set_shop_only_photo_geolocation_pushload/`, form, { signal: controller.signal });
      clearTimeout(id);

      refreshJourney().then(() => {
        // Backend already advanced the stage; reload /loan-application
        window.location.href = "/loan-application";
      }).catch(() => {
        // Still reload — backend already advanced the stage
        window.location.href = "/loan-application";
      });
    } catch {
      setTimeout(() => {
        navigate("/final-offer"); // Graceful fallback per original logic
      }, 150);
    } finally {
      setIsSubmitting(false);
      setGlobalLoading(false);
    }
  };

  // -- RENDER --

  if (loadingInitial || !backendData) return <FullPageLoader message="Fetching shop details..." />;

  // 1. Instructions
  if (isBuilderMode) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
        <div className="text-2xl mb-1">🏪</div>
        <p className="font-semibold text-gray-500">Shop Photo & Location</p>
        <p className="text-xs mt-1">Full-screen shop photo capture with GPS geo-location verification. Uploads to backend and advances journey.</p>
      </div>
    );
  }

  if (step === "instructions") {
    return (
      <InstructionsScreen
        address={backendData.address}
        thresholds={backendData.thresholds}
        onProceed={handleStartVerification}
        isChecking={isCheckingPermissions}
      />
    );
  }

  // 2. Permissions Logic
  if (step === "permissions") {
    // Show Location Request if denied/unknown
    if (permissionStatus.location !== "granted") {
      return (
        <PermissionRequestScreen
          type="location"
          onGrant={() => {
            navigator.geolocation.getCurrentPosition(
              () => {
                setPermissionStatus(p => ({ ...p, location: "granted" }));
                startWatching();
              },
              (e) => {
                console.warn(e);
                setPermissionStatus(p => ({ ...p, location: "denied" }));
              },
              { enableHighAccuracy: true }
            );
          }}
          onInstruction={() => showIOSHelp("location")}
        />
      );
    }
    // Show Camera Request if denied/unknown
    if (permissionStatus.camera !== "granted") {
      return (
        <PermissionRequestScreen
          type="camera"
          onGrant={async () => {
            try {
              const s = await navigator.mediaDevices.getUserMedia({ video: true });
              s.getTracks().forEach(t => t.stop());
              setPermissionStatus(p => ({ ...p, camera: "granted" }));
            } catch {
              setPermissionStatus(p => ({ ...p, camera: "denied" }));
            }
          }}
          onInstruction={() => showIOSHelp("camera")}
        />
      );
    }
  }

  // 3. Capture & Preview
  return (
    <div className="fixed inset-0 bg-black flex flex-col">
      {/* HEADER */}
      <div className="absolute top-0 inset-x-0 z-20 bg-gradient-to-b from-black/80 to-transparent p-4 pt-6">
        <h2 className="text-white text-center font-semibold text-lg drop-shadow-md">
          {step === "preview" ? "Verify Photo" : "Take Shop Photo"}
        </h2>
      </div>

      <div className="relative flex-1 bg-gray-900 overflow-hidden">
        {step === "capture" ? (
          <>
            <Webcam
              key={webcamKey}
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={VIDEO_CONSTRAINTS}
              className="absolute inset-0 w-full h-full object-cover"
              onUserMediaError={() => setPermissionStatus(p => ({ ...p, camera: "denied" }))}
            />
            {/* Guide Overlay */}
            <div className="absolute inset-0 border-[40px] border-black/40 pointer-events-none flex items-center justify-center">
              <div className="w-full h-px bg-white/30 absolute" />
              <div className="h-full w-px bg-white/30 absolute" />
              <p className="absolute top-4 text-white/70 text-xs font-bold uppercase tracking-widest bg-black/20 px-2 py-1 rounded">
                Center Shop Board
              </p>
            </div>

            {/* Live Stats */}
            <div className="absolute bottom-6 inset-x-6 flex flex-col gap-4 z-30">
              <div className="bg-black/60 backdrop-blur-md rounded-xl p-3 flex items-center justify-between text-xs text-white border border-white/10">
                {geoPointMissing ? (
                  <div className="flex items-center gap-2 w-full justify-center">
                    <div className="w-2 h-2 rounded-full bg-yellow-400" />
                    <span>Location not required for this address</span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${(!position || geoError) ? "bg-red-500 animate-pulse" : "bg-green-500"}`} />
                      <span>{geoError || (position ? `Accuracy: ${Math.round(position.accuracy)}m` : "Locating...")}</span>
                    </div>
                    {position && (
                      <span className="font-mono">{getDist(position.lat, position.lon)}m away</span>
                    )}
                  </>
                )}
              </div>

              <div className="flex justify-center">
                <button
                  onClick={handleCapture}
                  disabled={!geoPointMissing && (!!geoError || !position)}
                  className="w-20 h-20 rounded-full bg-white border-4 border-gray-300 shadow-lg active:scale-95 transition-all flex items-center justify-center disabled:opacity-50 disabled:scale-100"
                >
                  <div className="w-16 h-16 rounded-full border-2 border-black" />
                </button>
              </div>
            </div>
          </>
        ) : (
          capture && (
            <div className="relative w-full h-full flex flex-col">
              <img src={capture.dataUrl} alt="Preview" className="flex-1 object-cover" />

              {/* Validation Status Overlay */}
              <div className="absolute bottom-0 inset-x-0 bg-black/80 backdrop-blur-xl p-6 rounded-t-2xl z-40 border-t border-white/10">
                <div className="flex flex-col items-center text-center gap-2 mb-6">
                  {(geoPointMissing || capture.distanceMeters <= backendData.thresholds.THRESHOLD_METERS) ? (
                    <div className="w-12 h-12 rounded-full bg-green-500/20 text-green-500 flex items-center justify-center mb-1">
                      <CheckCircle2 className="w-6 h-6" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-red-500/20 text-red-500 flex items-center justify-center mb-1">
                      <AlertCircle className="w-6 h-6" />
                    </div>
                  )}

                  <h3 className="text-white text-lg font-semibold">
                    {geoPointMissing
                      ? "Photo Captured"
                      : capture.distanceMeters <= backendData.thresholds.THRESHOLD_METERS
                        ? "Location Verified"
                        : "Too Far from Shop"}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {geoPointMissing
                      ? "Address coordinates not available — location check skipped."
                      : `Captured ${capture.distanceMeters}m from registered address.${capture.distanceMeters > backendData.thresholds.THRESHOLD_METERS ? ` (Limit: ${backendData.thresholds.THRESHOLD_METERS}m)` : ""}`}
                  </p>
                </div>

                <div className="flex gap-4">
                  <Button variant="secondary" className="flex-1 h-12" onClick={() => {
                    setCapture(null);
                    setStep("capture");
                  }}>
                    <RefreshCw className="mr-2 w-4 h-4" /> Retake
                  </Button>
                  <Button
                    className={`flex-1 h-12 ${(!geoPointMissing && capture.distanceMeters > backendData.thresholds.THRESHOLD_METERS) ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}`}
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Uploading..." : "Submit Photo"}
                  </Button>
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}
